import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  type BottomSheetBackgroundProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Pressable, Text as RNText, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { SHEET_MAX_WIDTH } from './bounds';
import { cn } from './cn';
import { SheetContext } from './sheet-context';

export interface SheetProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Hide the ✕ close button (defaults to shown). */
  hideClose?: boolean;
  className?: string;
  /**
   * Optional shorter detent(s) to open at. When set, the sheet opens at this
   * height and expands (drag or scroll) up to its content height. Omit for the
   * default behavior of sizing straight to content. Pass a stable reference
   * (module constant / memoized) to avoid re-computing the detents each render.
   */
  snapPoints?: (string | number)[];
}

/** Bottom sheet — start-a-run, share, paywall, notes. */
export function Sheet({
  visible,
  onClose,
  title,
  children,
  hideClose,
  className,
  snapPoints,
}: SheetProps) {
  const ref = useRef<BottomSheetModal>(null);
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();

  // Cap the floating card at a dialog width and center it on wide (web/tablet)
  // windows by growing the horizontal margin; phones keep the 12px gutter.
  const sideInset = Math.max(12, (width - SHEET_MAX_WIDTH) / 2);

  // Cap the dynamic height so a long list (many templates, invite links) can't
  // grow the detached card past the top of the screen. Leave the top safe-area
  // clear and mirror the bottom float (`bottomInset` below), plus a small gap;
  // beyond this the inner BottomSheetScrollView scrolls instead of growing.
  const maxHeight = height - insets.top - insets.bottom - 48;

  // Bridge the controlled `visible` prop to the imperative modal. `presented`
  // mirrors the modal's mounted state so the effect only ever present()s a
  // closed modal and dismiss()es an open one. Without this guard, a
  // gorhom-initiated close (backdrop tap / swipe) fires onDismiss -> onClose ->
  // visible=false, and the effect then calls a *second*, redundant dismiss() on
  // the already-closing modal. That extra teardown corrupts the modal so the
  // next present() no-ops — the symptom being "the sheet opens once, but after
  // dismissing it won't reopen until reload."
  const presented = useRef(false);
  useEffect(() => {
    if (visible && !presented.current) {
      presented.current = true;
      ref.current?.present();
    } else if (!visible && presented.current) {
      presented.current = false;
      ref.current?.dismiss();
    }
  }, [visible]);

  // gorhom-initiated close: keep `presented` in sync up-front so the
  // visible=false effect above sees an already-closed modal and skips the
  // redundant dismiss(), then propagate the close to the controlled parent.
  const handleDismiss = useCallback(() => {
    presented.current = false;
    onClose();
  }, [onClose]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        opacity={0.4}
        pressBehavior="close"
      />
    ),
    [],
  );

  // Detached sheets float as a fully-rounded card; render the surface ourselves
  // so it inherits the Uniwind theme (bg-background, border) like the rest of the app.
  const renderBackground = useCallback(
    ({ style }: BottomSheetBackgroundProps) => (
      <View style={style} className="rounded-3xl border border-border bg-background" />
    ),
    [],
  );

  const renderHandle = useCallback(
    () => (
      <View className="items-center pb-2 pt-3">
        <View className="h-1 w-10 rounded-full bg-border-strong" />
      </View>
    ),
    [],
  );

  return (
    <BottomSheetModal
      ref={ref}
      detached
      bottomInset={insets.bottom + 12}
      style={{ marginHorizontal: sideInset }}
      snapPoints={snapPoints}
      enableDynamicSizing
      maxDynamicContentSize={maxHeight}
      keyboardBehavior="interactive"
      android_keyboardInputMode="adjustResize"
      onDismiss={handleDismiss}
      backdropComponent={renderBackdrop}
      backgroundComponent={renderBackground}
      handleComponent={renderHandle}
    >
      <BottomSheetScrollView keyboardShouldPersistTaps="handled">
        <SheetContext.Provider value={true}>
          <View className={cn('px-5 pb-8', className)}>
            {title || !hideClose ? (
              <View className="mb-3 flex-row items-center justify-between">
                {title ? (
                  <RNText className="font-heading text-xl text-foreground">
                    {title}
                  </RNText>
                ) : (
                  <View />
                )}
                {!hideClose ? (
                  <Pressable
                    onPress={() => ref.current?.dismiss()}
                    hitSlop={10}
                    accessibilityRole="button"
                    accessibilityLabel="Close"
                    className="h-8 w-8 items-center justify-center"
                  >
                    <RNText className="text-lg text-muted">✕</RNText>
                  </Pressable>
                ) : null}
              </View>
            ) : null}

            {children}
          </View>
        </SheetContext.Provider>
      </BottomSheetScrollView>
    </BottomSheetModal>
  );
}
